import React, { useEffect, useMemo, useState } from "react";

import api from "../services/api";
import { qlAlert } from "../utils/dialogs";
import {
  JOURS, MODALITES, STATUT_PROFIL, disponibilitesLisibles, fcfa, initiales, libelleModalites, liste, messageErreur,
} from "../components/soutien/soutienCommun";
import "../components/soutien/soutien.css";
import { SeancesLecture } from "../components/soutien/Seances";

// « Mon profil de soutien scolaire » de l'enseignant générateur : 4 sections
// (Informations, Prestations et tarifs, Disponibilités, Publication) et un
// aperçu fidèle à la fiche vue par les parents. Enregistrer ≠ Soumettre.

const SECTIONS = [
  ["informations", "Informations", "bi-person"],
  ["prestations", "Prestations et tarifs", "bi-journal-bookmark"],
  ["disponibilites", "Disponibilités", "bi-calendar-week"],
  ["publication", "Publication", "bi-shield-check"],
];
const FORMATS_PHOTO = ["image/jpeg", "image/png", "image/webp"];
const PHOTO_MAX = 5 * 1024 * 1024;

const versHeure = (valeur) => {
  if (!valeur) return "";
  const [h, m] = valeur.split(":");
  return m && m !== "00" ? `${Number(h)}h${m}` : `${Number(h)}h`;
};
const depuisHeure = (texte) => {
  const m = /^(\d{1,2})h(\d{2})?$/.exec(String(texte || "").trim());
  return m ? `${m[1].padStart(2, "0")}:${m[2] || "00"}` : "";
};
const creneauxVides = () => Object.fromEntries(JOURS.map(([cle]) => [cle, { actif: false, debut: "", fin: "" }]));
const versCreneaux = (dispo) => {
  const creneaux = creneauxVides();
  Object.entries(dispo || {}).forEach(([jour, plage]) => {
    if (!creneaux[jour]) return;
    const [debut, fin] = String(plage || "").split("-");
    creneaux[jour] = { actif: true, debut: depuisHeure(debut), fin: depuisHeure(fin) };
  });
  return creneaux;
};
const versDispo = (creneaux) => Object.fromEntries(Object.entries(creneaux)
  .filter(([, c]) => c.actif && c.debut && c.fin).map(([jour, c]) => [jour, `${versHeure(c.debut)}-${versHeure(c.fin)}`]));

// Erreurs par section (validation avant envoi ; le serveur revérifie tout).
export function verifierProfil(form, creneaux, tarifs, { pourSoumettre = false } = {}) {
  const erreurs = { informations: [], prestations: [], disponibilites: [] };
  if (!form.full_name.trim()) erreurs.informations.push("Indiquez le nom affiché.");
  if (!form.independent && !form.establishment.trim()) erreurs.informations.push("Indiquez votre établissement ou choisissez « Intervenant indépendant ».");
  if (pourSoumettre && !form.city.trim()) erreurs.informations.push("Indiquez votre ville.");
  if (pourSoumettre && !form.presentation.trim()) erreurs.informations.push("Rédigez votre présentation professionnelle.");
  if (pourSoumettre && !form.subject_ids.length) erreurs.prestations.push("Choisissez au moins une matière.");
  if (pourSoumettre && !form.class_ids.length) erreurs.prestations.push("Choisissez au moins une classe.");
  if (pourSoumettre && !form.modalities.length) erreurs.prestations.push("Choisissez au moins une modalité de cours.");
  form.subject_ids.forEach((id) => {
    const valeur = tarifs[id];
    if (valeur !== "" && valeur != null && (!Number.isFinite(Number(valeur)) || Number(valeur) < 0)) {
      erreurs.prestations.push("Chaque tarif doit être un nombre positif.");
    }
  });
  let valides = 0;
  JOURS.forEach(([cle, nom]) => {
    const c = creneaux[cle];
    if (!c.actif) return;
    if (!c.debut || !c.fin) erreurs.disponibilites.push(`${nom} : indiquez l'heure de début et de fin.`);
    else if (c.fin <= c.debut) erreurs.disponibilites.push(`${nom} : l'heure de fin doit être après l'heure de début.`);
    else valides += 1;
  });
  if (pourSoumettre && !valides) erreurs.disponibilites.push("Indiquez au moins une plage de disponibilité.");
  erreurs.prestations = [...new Set(erreurs.prestations)];
  return erreurs;
}

function Apercu({ form, matieres, classes, tarifs, creneaux, photo, statut }) {
  const sujets = matieres.filter((m) => form.subject_ids.includes(m.id));
  const niveaux = classes.filter((c) => form.class_ids.includes(c.id));
  const prix = sujets.filter((m) => Number(tarifs[m.id]) > 0);
  const dispo = disponibilitesLisibles(versDispo(creneaux));
  return (
    <aside className="sc-card sc-apercu" aria-label="Aperçu dans l'annuaire">
      <h2>Aperçu dans l'annuaire</h2>
      {photo
        ? <img className="sc-avatar sc-avatar--grand" src={photo} alt="" />
        : <span className="sc-avatar sc-avatar--grand" aria-hidden="true">{initiales(form.full_name)}</span>}
      <strong className="sc-apercu-nom">{form.full_name || "Nom du professeur"}</strong>
      <p className="sc-muted sc-petit">{[form.independent ? "Intervenant indépendant" : form.establishment, form.city, form.zone].filter(Boolean).join(" · ")}</p>
      {statut === "approved" && <span className="sc-badge">✓ Profil validé</span>}
      {form.presentation && <p className="sc-petit">{form.presentation}</p>}
      {sujets.length > 0 && <><strong className="sc-petit">Matières</strong><div className="sc-pastilles">{sujets.map((m) => <span className="sc-pill" key={m.id}>{m.name}</span>)}</div></>}
      {niveaux.length > 0 && <><strong className="sc-petit">Classes</strong><div className="sc-pastilles">{niveaux.map((c) => <span className="sc-pill" key={c.id}>{c.name}</span>)}</div></>}
      {form.modalities.length > 0 && <p className="sc-petit sc-muted">{libelleModalites(form.modalities).join(" · ")}</p>}
      <strong className="sc-petit">Tarifs indicatifs</strong>
      <p className="sc-petit">{prix.length ? prix.map((m) => `${m.name} : ${fcfa(tarifs[m.id])}/h`).join(" · ") : <span className="sc-muted">Non renseignés (aucun prix affiché aux parents).</span>}</p>
      {dispo.length > 0 && <ul className="sc-dispos sc-petit">{dispo.map(([j, p]) => <li key={j}><strong>{j}</strong> {p}</li>)}</ul>}
      <p className="sc-petit sc-muted">Vos coordonnées privées ne sont jamais affichées aux parents.</p>
    </aside>
  );
}

export default function TeacherSupportProfile({ onBack }) {
  const [matieres, setMatieres] = useState([]);
  const [classes, setClasses] = useState([]);
  const [form, setForm] = useState({ full_name: "", establishment: "", independent: false, presentation: "", city: "", zone: "", modalities: [], subject_ids: [], class_ids: [] });
  const [creneaux, setCreneaux] = useState(creneauxVides);
  const [tarifs, setTarifs] = useState({});
  const [profil, setProfil] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [apercuPhoto, setApercuPhoto] = useState(null);
  const [section, setSection] = useState("informations");
  const [chargement, setChargement] = useState(true);
  const [erreurChargement, setErreurChargement] = useState("");
  const [erreurs, setErreurs] = useState({ informations: [], prestations: [], disponibilites: [] });
  const [confirme, setConfirme] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  const appliquer = (data) => {
    if (!data) return;
    setForm({
      full_name: data.full_name || "", establishment: data.establishment || "", independent: !!data.independent,
      presentation: data.presentation || "", city: data.city || "", zone: data.zone || "", modalities: data.modalities || [],
      subject_ids: (data.subjects || []).map((x) => x.id), class_ids: (data.classes || []).map((x) => x.id),
    });
    setCreneaux(versCreneaux(data.availability));
    setTarifs(data.hourly_rates || {});
    setProfil(data);
    setApercuPhoto(data.photo_url || null);
  };

  useEffect(() => {
    api.get("matieres/").then((r) => setMatieres(liste(r.data))).catch(() => {});
    api.get("classes/").then((r) => setClasses(liste(r.data))).catch(() => {});
    api.get("parent-teacher/teacher-profile/me/")
      .then((r) => appliquer(r.data))
      .catch((e) => setErreurChargement(e?.response?.status === 403 ? "Le soutien scolaire n'est pas disponible pour ce compte." : messageErreur(e)))
      .finally(() => setChargement(false));
  }, []);

  useEffect(() => () => { if (apercuPhoto?.startsWith("blob:")) URL.revokeObjectURL(apercuPhoto); }, [apercuPhoto]);

  const maj = (cle) => (e) => setForm((f) => ({ ...f, [cle]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const basculer = (cle, valeur) => setForm((f) => ({ ...f, [cle]: f[cle].includes(valeur) ? f[cle].filter((x) => x !== valeur) : [...f[cle], valeur] }));
  const majCreneau = (jour, patch) => setCreneaux((c) => ({ ...c, [jour]: { ...c[jour], ...patch } }));

  const choisirPhoto = (e) => {
    const fichier = e.target.files?.[0] || null;
    if (!fichier) return;
    if (!FORMATS_PHOTO.includes(fichier.type)) { qlAlert("Formats acceptés : JPEG, PNG ou WebP.", "error"); e.target.value = ""; return; }
    if (fichier.size > PHOTO_MAX) { qlAlert("La photo ne doit pas dépasser 5 Mo.", "error"); e.target.value = ""; return; }
    setPhoto(fichier);
    setApercuPhoto(URL.createObjectURL(fichier));
  };

  const controler = (pourSoumettre) => {
    const resultat = verifierProfil(form, creneaux, tarifs, { pourSoumettre });
    setErreurs(resultat);
    const premiere = SECTIONS.find(([cle]) => resultat[cle]?.length);
    if (premiere) { setSection(premiere[0]); window.scrollTo({ top: 0, behavior: "smooth" }); return false; }
    return true;
  };

  const enregistrer = async ({ silencieux = false } = {}) => {
    const hourly_rates = Object.fromEntries(form.subject_ids.map(String)
      .map((id) => [id, Number(tarifs[id])]).filter(([, v]) => Number.isFinite(v) && v > 0));
    let r = await api.put("parent-teacher/teacher-profile/me/", { ...form, availability: versDispo(creneaux), hourly_rates });
    if (photo) {
      const data = new FormData();
      data.append("photo", photo);
      r = await api.patch("parent-teacher/teacher-profile/me/", data, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(null);
    }
    appliquer(r.data);
    if (!silencieux) qlAlert("Brouillon enregistré.", "success");
    return r.data;
  };

  const surEnregistrer = async () => {
    if (!controler(false)) return;
    setEnvoi(true);
    try { await enregistrer(); } catch (err) { qlAlert(messageErreur(err), "error"); } finally { setEnvoi(false); }
  };

  const surSoumettre = async () => {
    if (!controler(true)) return;
    if (!confirme) { qlAlert("Confirmez que les informations et tarifs indiqués sont exacts.", "warning"); return; }
    setEnvoi(true);
    try {
      await enregistrer({ silencieux: true });
      const r = await api.post("parent-teacher/teacher-profile/me/");
      appliquer(r.data);
      setConfirme(false);
      qlAlert("Votre profil a été envoyé pour validation. L'administration va l'examiner.", "success");
    } catch (err) { qlAlert(messageErreur(err), "error"); } finally { setEnvoi(false); }
  };

  const statut = profil?.status;
  const suivante = useMemo(() => Object.fromEntries(SECTIONS.map(([cle], i) => [cle, SECTIONS[i + 1]?.[0]])), []);
  const Erreurs = ({ cle }) => (erreurs[cle]?.length
    ? <div className="sc-alerte" role="alert"><ul className="mb-0 ps-3">{erreurs[cle].map((e) => <li key={e}>{e}</li>)}</ul></div> : null);
  const Continuer = ({ cle }) => (
    <div className="sc-ligne sc-actions-form">
      <button type="button" className="sc-btn sc-btn--navy" onClick={() => { setSection(suivante[cle]); window.scrollTo({ top: 0, behavior: "smooth" }); }}>Continuer →</button>
    </div>
  );

  if (chargement) return <main className="sc-profil-ens"><div className="sc-etat" role="status"><span className="spinner-border spinner-border-sm" /> Chargement…</div></main>;
  if (erreurChargement) {
    return <main className="sc-profil-ens"><div className="sc-etat sc-etat--erreur" role="alert">{erreurChargement}
      {onBack && <button type="button" className="sc-btn sc-btn--outline" onClick={onBack}>← Retour</button>}</div></main>;
  }

  return (
    <main className="sc-profil-ens">
      <div className="sc-profil-ens-tete">
        <div>
          {onBack && <button type="button" className="sc-btn sc-btn--lien" onClick={onBack}>← Retour</button>}
          <h1>Mon profil de soutien scolaire</h1>
          <p className="sc-muted">Complétez vos informations pour être découvert par les parents. Prévisualisez votre fiche avant de demander sa publication.</p>
        </div>
        <span className={`sc-statut-profil sc-statut-profil--${statut || "nouveau"}`}>{statut ? STATUT_PROFIL[statut] : "Nouveau profil"}</span>
      </div>

      <div className="sc-profil-ens-grille">
        <nav className="sc-card sc-profil-ens-nav" aria-label="Sections du profil">
          {SECTIONS.map(([cle, libelle, icone]) => (
            <button type="button" key={cle} className={section === cle ? "actif" : ""} aria-current={section === cle ? "step" : undefined} onClick={() => setSection(cle)}>
              <i className={`bi ${icone}`} aria-hidden="true"></i>{libelle}
              {erreurs[cle]?.length > 0 && <span className="sc-pastille-erreur" aria-label="À corriger">!</span>}
            </button>
          ))}
        </nav>

        <section className="sc-card" aria-live="polite">
          {section === "informations" && <>
            <h2>Informations du professeur</h2>
            <Erreurs cle="informations" />
            <div className="sc-ligne-form">
              <div className="sc-champ"><label htmlFor="sp-nom">Nom affiché</label><input id="sp-nom" required maxLength={200} value={form.full_name} onChange={maj("full_name")} /></div>
              <div className="sc-champ"><label htmlFor="sp-photo">Photo de profil</label><input id="sp-photo" type="file" accept="image/jpeg,image/png,image/webp" onChange={choisirPhoto} /><span className="sc-petit sc-muted">JPEG, PNG ou WebP, 5 Mo maximum.</span></div>
            </div>
            <div className="sc-ligne-form">
              <div className="sc-champ"><label htmlFor="sp-statut">Statut professionnel</label>
                <select id="sp-statut" value={form.independent ? "independant" : "etablissement"} onChange={(e) => setForm((f) => ({ ...f, independent: e.target.value === "independant" }))}>
                  <option value="etablissement">Enseignant en établissement</option>
                  <option value="independant">Intervenant indépendant</option>
                </select></div>
              <div className="sc-champ"><label htmlFor="sp-etab">Établissement{form.independent ? " (facultatif)" : ""}</label><input id="sp-etab" maxLength={200} value={form.establishment} onChange={maj("establishment")} /></div>
            </div>
            <div className="sc-ligne-form">
              <div className="sc-champ"><label htmlFor="sp-ville">Ville</label><input id="sp-ville" maxLength={120} value={form.city} onChange={maj("city")} /></div>
              <div className="sc-champ"><label htmlFor="sp-zone">Quartier / zone d'intervention (facultatif)</label><input id="sp-zone" maxLength={120} value={form.zone} onChange={maj("zone")} placeholder="Ex. : Ngoa-Ekellé" /></div>
            </div>
            <div className="sc-champ"><label htmlFor="sp-bio">Présentation professionnelle</label><textarea id="sp-bio" rows={5} value={form.presentation} onChange={maj("presentation")} /></div>
            <Continuer cle="informations" />
          </>}

          {section === "prestations" && <>
            <h2>Matières et classes enseignées</h2>
            <p className="sc-muted">Sélectionnez les matières, puis indiquez si vous le souhaitez un tarif horaire pour chacune.</p>
            <Erreurs cle="prestations" />
            <fieldset className="sc-champ"><legend>Matières</legend>
              <div className="sc-cases">{matieres.map((m) => <label key={m.id} className="sc-case"><input type="checkbox" checked={form.subject_ids.includes(m.id)} onChange={() => basculer("subject_ids", m.id)} /> {m.name}</label>)}</div>
            </fieldset>
            <fieldset className="sc-champ"><legend>Classes prises en charge</legend>
              <div className="sc-cases">{classes.map((c) => <label key={c.id} className="sc-case"><input type="checkbox" checked={form.class_ids.includes(c.id)} onChange={() => basculer("class_ids", c.id)} /> {c.name}</label>)}</div>
            </fieldset>
            {form.subject_ids.length > 0 && <fieldset className="sc-champ"><legend>Tarifs horaires (FCFA)</legend>
              {form.subject_ids.map((id) => {
                const m = matieres.find((x) => x.id === id);
                if (!m) return null;
                return <div className="sc-tarif" key={id}><label htmlFor={`sp-tarif-${id}`}>{m.name}</label>
                  <input id={`sp-tarif-${id}`} type="number" min="0" step="100" inputMode="numeric" placeholder="Non renseigné" value={tarifs[id] ?? ""} onChange={(e) => setTarifs((t) => ({ ...t, [id]: e.target.value }))} /></div>;
              })}
              <p className="sc-petit sc-muted">Laissez vide pour ne pas afficher de prix pour cette matière.</p>
            </fieldset>}
            <fieldset className="sc-champ"><legend>Modalités de cours</legend>
              <div className="sc-cases">{MODALITES.map(([cle, libelle]) => <label key={cle} className="sc-case"><input type="checkbox" checked={form.modalities.includes(cle)} onChange={() => basculer("modalities", cle)} /> {libelle}</label>)}</div>
            </fieldset>
            <Continuer cle="prestations" />
          </>}

          {section === "disponibilites" && <>
            <h2>Disponibilités hebdomadaires</h2>
            <p className="sc-muted">Activez les jours où vous êtes disponible et renseignez une plage horaire.</p>
            <Erreurs cle="disponibilites" />
            <div className="sc-jours">
              {JOURS.map(([cle, nom]) => (
                <div className={`sc-jour${creneaux[cle].actif ? " actif" : ""}`} key={cle}>
                  <label className="sc-case"><input type="checkbox" checked={creneaux[cle].actif} onChange={(e) => majCreneau(cle, { actif: e.target.checked })} /> {nom}</label>
                  <input type="time" aria-label={`${nom} : début`} disabled={!creneaux[cle].actif} value={creneaux[cle].debut} onChange={(e) => majCreneau(cle, { debut: e.target.value })} />
                  <input type="time" aria-label={`${nom} : fin`} disabled={!creneaux[cle].actif} value={creneaux[cle].fin} onChange={(e) => majCreneau(cle, { fin: e.target.value })} />
                </div>
              ))}
            </div>
            <Continuer cle="disponibilites" />
          </>}

          {section === "publication" && <>
            <h2>Validation et publication</h2>
            {statut === "approved" && <div className="sc-succes">✓ Votre profil public est approuvé et visible par les parents.</div>}
            {statut === "pending" && <div className="sc-notice">Votre profil est en cours d'examen par l'administration.</div>}
            {statut === "rejected" && <div className="sc-alerte">Dernière soumission refusée{profil?.decision_reason ? ` : ${profil.decision_reason}` : "."}</div>}
            {statut === "suspended" && <div className="sc-alerte">Profil suspendu{profil?.decision_reason ? ` : ${profil.decision_reason}` : "."} Il n'est plus visible des parents.</div>}
            {profil?.version_publiee_visible && statut !== "approved" && (
              <div className="sc-succes">Votre version déjà approuvée reste visible par les parents jusqu'à la décision sur vos modifications.</div>
            )}
            <p className="sc-muted">« Enregistrer le brouillon » garde vos modifications sans les publier. « Soumettre à validation » les envoie à l'administration ; elles deviennent publiques seulement après approbation.</p>
            <label className="sc-case"><input type="checkbox" checked={confirme} onChange={(e) => setConfirme(e.target.checked)} /> Je confirme que les informations et tarifs indiqués sont exacts.</label>
            <div className="sc-ligne sc-actions-form">
              <button type="button" className="sc-btn sc-btn--outline" disabled={envoi} onClick={surEnregistrer}>Enregistrer le brouillon</button>
              <button type="button" className="sc-btn sc-btn--or" disabled={envoi} onClick={surSoumettre}>{envoi ? "Envoi…" : "Soumettre à validation"}</button>
            </div>
          </>}
          {section !== "publication" && (
            <div className="sc-ligne sc-actions-form sc-actions-secondaires">
              <button type="button" className="sc-btn sc-btn--lien" disabled={envoi} onClick={surEnregistrer}>Enregistrer le brouillon</button>
            </div>
          )}
        </section>

        <Apercu form={form} matieres={matieres} classes={classes} tarifs={tarifs} creneaux={creneaux} photo={apercuPhoto} statut={statut} />
      </div>

      {profil && (
        <section className="sc-card sc-seances-ens" aria-label="Séances et paiements">
          <h2>Séances et paiements</h2>
          <p className="sc-muted">Les parents déclarent chaque séance (jour, début, fin). Montant du mois = heures déclarées × votre tarif horaire de la matière.
            Une séance qui ne correspond pas à un cours donné se signale à l'administration par la messagerie.</p>
          <SeancesLecture url="parent-teacher/teacher-sessions/" colonnes={["parent_name", "student_name"]} />
        </section>
      )}
    </main>
  );
}
